'use client'

import React, { useState, useMemo, useEffect, Suspense } from 'react'
import { useRouter, usePathname, useSearchParams } from 'next/navigation'
import AdvisoryTile from './AdvisoryTile'
import {
  Advisory,
  AdvisoryGraphQLItem,
  SecurityAdvisoriesGraphQLData,
  PageParagraphItem,
} from './SecurityAdvisories.interface'
import Image from 'next/image'
import Footer from '@/src/components/commons/Footer/Footer'
import PageHeader from '@/src/components/commons/generics/PageHeader/PageHeader'
import LandingPageContainer from '@/src/components/commons/generics/LandingPageContainer/LandingPageContainer'

// --- GraphQL Imports ---
import { GET_ADVISORIES_QUERY } from './securityAdvisories.graphql'
import { ApolloProvider, useQuery } from '@apollo/client/react'
import { client } from '@/src/utils/datalayer/apolloClient'
// --- End GraphQL Imports ---

import IconAngleRightRed from '../../../public/assets/images/icon_angle_right_red.svg'
import styles from './SecurityAdvisories.module.scss'
import { normalizeTextContent } from '@/src/utils/common'

const RESULTS_PER_PAGE = 15

// --- Utility Functions (extractSummary, mapAdvisories) ---
const extractSummary = (fieldPageParagraph: PageParagraphItem[] | null | undefined): string => {
  if (!fieldPageParagraph || fieldPageParagraph.length === 0) {
    return 'Summary not available.'
  }
  const pageParagraphItem = fieldPageParagraph[0]
  const deepContent =
    pageParagraphItem?.entity?.fieldReusableParagraphs[0]?.entity?.fieldWysiwygModContent?.value
  if (!deepContent) return 'Summary not available.'
  const cleanText = normalizeTextContent(deepContent)
  const maxLength = 150
  return cleanText.substring(0, maxLength).trim() + (cleanText.length > maxLength ? '...' : '')
}

const mapAdvisories = (data: SecurityAdvisoriesGraphQLData | undefined): Advisory[] => {
  if (!data?.advisoriesGraphql?.entities) return []
  const entities = data.advisoriesGraphql.entities
  return entities.map((item: AdvisoryGraphQLItem) => ({
    id: String(item.nid),
    title: item.title,
    publishDate: item.fieldDate.value || '',
    summary: extractSummary(item.fieldPageParagraph),
    link: item.path.alias || `/advisories/${item.nid}`,
  }))
}

// Main component that will fetch and display advisories
const SecurityAdvisoriesContent: React.FC = () => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  // Initialize state from URL search params
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '')
  const [currentPage, setCurrentPage] = useState(() => Number(searchParams.get('page')) || 1)
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(
    () => searchParams.get('search') || '',
  )
  const [isMounted, setIsMounted] = useState(false) // Prevents sync effect on initial load

  // DEBOUNCE EFFECT (2000ms delay)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim())
    }, 2000)
    return () => {
      clearTimeout(handler)
    }
  }, [searchTerm])

  // Calculate the offset for the GraphQL query
  const offset = useMemo(() => (currentPage - 1) * RESULTS_PER_PAGE, [currentPage])

  // Use Apollo's useQuery hook to fetch data
  const { loading, error, data } = useQuery<SecurityAdvisoriesGraphQLData>(GET_ADVISORIES_QUERY, {
    variables: {
      search: debouncedSearchTerm || undefined,
      limit: RESULTS_PER_PAGE,
      offset: offset,
    },
    fetchPolicy: 'cache-and-network',
  })

  // Map the raw GraphQL data to the simplified Advisory interface
  const advisories = useMemo(() => mapAdvisories(data), [data])

  // Get total count and calculate total pages from the GraphQL response
  const totalResults = data?.advisoriesGraphql?.count || 0
  const totalPages = Math.ceil(totalResults / RESULTS_PER_PAGE)

  // --- URL Synchronization ---

  // 1. Mount effect
  useEffect(() => {
    setIsMounted(true)
  }, [])

  // Effect to synchronize state TO the URL
  useEffect(() => {
    // Wait until component is mounted to prevent hydration mismatch
    if (!isMounted) return

    const params = new URLSearchParams()

    if (debouncedSearchTerm) {
      params.set('search', debouncedSearchTerm)
    }
    if (currentPage > 1) {
      params.set('page', currentPage.toString())
    }

    const queryString = params.toString()

    // Use router.push to update the URL and add to browser history
    // 'scroll: false' prevents the page from jumping to the top
    router.push(`${pathname}${queryString ? `?${queryString}` : ''}`, { scroll: false })
  }, [currentPage, debouncedSearchTerm, isMounted, pathname, router]) // Run when state changes

  // 3. Effect to reset page if filters make it invalid
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages)
    } else if (totalPages === 0 && currentPage !== 1 && !loading) {
      setCurrentPage(1)
    }
  }, [totalPages, currentPage, loading])

  // --- Handlers ---

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setCurrentPage(1) // Reset to page 1 on new search
  }

  // Render loading state
  if (loading && !advisories.length)
    return <p className="text-white text-center py-[50px] font-dm-mono">Loading advisories...</p>
  // Render error state
  if (error)
    return (
      <p className="text-white text-center py-[50px] font-dm-mono">
        Error loading advisories: {error.message}
      </p>
    )

  // Pagination Controls Component
  const PaginationControls: React.FC = () => {
    if (totalPages <= 1) return null

    const handlePageChange = (page: number) => {
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }

    const getDisplayPages = () => {
      const pages: (number | string)[] = []
      const delta = 3
      const left = currentPage - delta
      const right = currentPage + delta
      const lastPage = totalPages

      pages.push(1)
      if (left > 2) pages.push('...')
      for (let i = 2; i < lastPage; i++) {
        if (i >= left && i <= right) {
          pages.push(i)
        }
      }
      if (right < lastPage - 1) pages.push('...')
      if (lastPage > 1 && pages[pages.length - 1] !== lastPage) pages.push(lastPage)

      return pages.filter((item, index, array) => item !== '...' || array[index - 1] !== '...')
    }

    const displayPages = getDisplayPages()

    return (
      <div className={styles.pagination}>
        {/* Previous Button */}
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={styles.pageButton}
        >
          <Image
            src={IconAngleRightRed.src}
            alt="Previous Result Page"
            width={6}
            height={11}
            className={`inline-block! align-middle ${styles.prevPage}`}
          />
        </button>

        {/* Page Numbers */}
        {displayPages.map((page, index) =>
          page === '...' ? (
            <span key={index} className={styles.pageNumber}>
              ...
            </span>
          ) : (
            <button
              type="button"
              key={page}
              onClick={() => handlePageChange(page as number)}
              disabled={page === currentPage}
              aria-current={page === currentPage ? 'page' : undefined}
              className={page === currentPage ? styles.activePage : styles.pageNumber}
            >
              {page}
            </button>
          ),
        )}

        {/* Next Button */}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={styles.pageButton}
        >
          <Image
            src={IconAngleRightRed.src}
            alt="Next Result Page"
            width={6}
            height={11}
            className={`inline-block! align-middle ${styles.nextPage}`}
          />
        </button>
      </div>
    )
  }

  const pageHeading = 'Security Advisories'
  const pageSubHeading = 'The latest news, announcements, and perspectives from Zscaler.'

  return (
    <>
      <LandingPageContainer className={styles.pageWrapper}>
        <div className={styles.header}>
          <PageHeader
            heading={pageHeading}
            subheading={pageSubHeading}
            className="text-left! text-white! pb-10! mb-[30px]! border-b! border-[#3E4752]"
          />
        </div>

        {/* Search Input */}
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder="Search advisories"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>

        {/* Tiles Grid */}
        <div className={styles.tilesGrid}>
          {advisories.map((advisory) => (
            <AdvisoryTile key={advisory.id} advisory={advisory} />
          ))}

          {totalResults === 0 && !loading && (
            <p style={{ gridColumn: '1 / -1' }}>No advisories found matching your search term.</p>
          )}
        </div>

        {/* Pagination Controls */}
        <PaginationControls />
      </LandingPageContainer>
      <Footer />
    </>
  )
}

// Wrap the main content component with ApolloProvider to make the client available
const SecurityAdvisoriesPage: React.FC = () => (
  <ApolloProvider client={client}>
    {/* Must wrap in Suspense for useSearchParams to function */}
    <Suspense fallback={<div>Loading Page...</div>}>
      <SecurityAdvisoriesContent />
    </Suspense>
  </ApolloProvider>
)

export default SecurityAdvisoriesPage
